import React from 'react';
import { DiffAnalysis } from '../../types';
import {
  Sparkles,
  AlertTriangle,
  Scale,
  Shield,
  Brain,
  CreditCard,
  Ban,
  FileCode2,
  Lock,
  BookOpen,
  ExternalLink,
} from 'lucide-react';

interface AiSummaryCardProps {
  analysis: DiffAnalysis;
  onReadDocument?: () => void;
  documentUrl?: string;
}

export const AiSummaryCard: React.FC<AiSummaryCardProps> = ({
  analysis,
  onReadDocument,
  documentUrl,
}) => {
  // Map 8 categories to custom icon and styling
  const getCategoryMeta = (cat: string) => {
    switch (cat) {
      case 'AI_TRAINING_AND_DATA_USAGE':
        return { label: 'AI Training & Data Usage', icon: Brain, color: 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300 border-purple-300 dark:border-purple-800' };
      case 'DATA_COLLECTION_AND_PRIVACY':
        return { label: 'Data Collection & Privacy', icon: Shield, color: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300 border-blue-300 dark:border-blue-800' };
      case 'DISPUTE_RESOLUTION_AND_ARBITRATION':
        return { label: 'Arbitration & Disputes', icon: Scale, color: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 border-amber-300 dark:border-amber-800' };
      case 'PAYMENTS_SUBSCRIPTIONS_AND_BILLING':
        return { label: 'Payments & Billing', icon: CreditCard, color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800' };
      case 'ACCOUNT_TERMINATION_AND_SUSPENSION':
        return { label: 'Account Suspension', icon: Ban, color: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300 border-rose-300 dark:border-rose-800' };
      case 'USER_RIGHTS_AND_CONTENT_OWNERSHIP':
        return { label: 'Content Ownership & Rights', icon: Lock, color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300 border-indigo-300 dark:border-indigo-800' };
      default:
        return { label: cat.replace(/_/g, ' '), icon: FileCode2, color: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300 border-slate-300 dark:border-slate-700' };
    }
  };

  const headline = analysis.change_headline || analysis.headline || 'Policy Revision Update';
  const practicalImpact = analysis.practical_user_impact || analysis.practicalImpact;
  const keyChanges = analysis.key_changes || analysis.keyChanges || [];
  const primaryCats = analysis.primary_categories || analysis.primaryCategories || [];

  return (
    <div className="bg-gradient-to-br from-indigo-50/90 via-white to-purple-50/60 dark:from-slate-800 dark:via-slate-800 dark:to-indigo-950/30 rounded-xl border border-indigo-200 dark:border-indigo-900/60 p-5 shadow-2xs space-y-4">
      {/* Header with AI Badge & Document Links */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-indigo-100 dark:border-indigo-900/50">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-indigo-600 text-white shadow-sm">
            <Sparkles className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-900 dark:text-white">
              AI Executive Change Analysis
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Powered by Gemini AI • Structured Legal Extraction
            </p>
          </div>
        </div>

        {(onReadDocument || documentUrl) && (
          <div className="flex items-center gap-2">
            {onReadDocument && (
              <button
                onClick={onReadDocument}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-slate-700 border border-indigo-200 dark:border-indigo-800 transition-colors shadow-2xs cursor-pointer"
                title="Read complete document in reader"
              >
                <BookOpen className="w-3.5 h-3.5" />
                <span>Read Document</span>
              </button>
            )}
            {documentUrl && (
              <a
                href={documentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 transition-colors shadow-2xs"
                title="Open original live document"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                <span>Original Document</span>
              </a>
            )}
          </div>
        )}
      </div>

      {/* Change Headline */}
      <div className="bg-white dark:bg-slate-900/80 p-3.5 rounded-lg border border-slate-200 dark:border-slate-700/80">
        <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1">
          Revision Headline
        </h4>
        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 leading-snug">
          {headline}
        </p>
      </div>

      {/* 8-Category Badges */}
      {primaryCats.length > 0 && (
        <div className="space-y-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
            Primary Domains Affected
          </span>
          <div className="flex flex-wrap gap-2">
            {primaryCats.map((cat, idx) => {
              const meta = getCategoryMeta(cat);
              const Icon = meta.icon;
              return (
                <span
                  key={idx}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border ${meta.color}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {meta.label}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Practical User Impact Box */}
      {practicalImpact && (
        <div className="bg-amber-50 dark:bg-amber-950/30 border-l-4 border-amber-500 p-3.5 rounded-r-lg text-xs">
          <div className="flex items-center gap-1.5 font-bold text-amber-900 dark:text-amber-300 mb-1">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            Practical End-User Impact
          </div>
          <p className="text-amber-800 dark:text-amber-200 leading-relaxed">
            {practicalImpact}
          </p>
        </div>
      )}

      {/* Key Changes Bullets */}
      {keyChanges.length > 0 && (
        <div className="space-y-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
            Key Modifications
          </span>
          <ul className="space-y-1.5 text-xs text-slate-700 dark:text-slate-300 pl-4 list-disc">
            {keyChanges.map((change, idx) => (
              <li key={idx} className="leading-relaxed">
                {change}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
